import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  HelpOutline,
  Refresh,
  Storage,
  Cloud,
  Memory,
  Speed,
  Timer,
} from '@mui/icons-material';

import { useSystemHealth } from '../../hooks/useSystem';
import type { ServiceHealth } from '../../api';

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h ${mins}m`;
  }
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'healthy':
      return <CheckCircle sx={{ color: '#10b981' }} />;
    case 'degraded':
      return <Warning sx={{ color: '#f59e0b' }} />;
    case 'unhealthy':
      return <Error sx={{ color: '#ef4444' }} />;
    default:
      return <HelpOutline sx={{ color: '#6b7280' }} />;
  }
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'healthy':
      return 'success';
    case 'degraded':
      return 'warning';
    case 'unhealthy':
      return 'error';
    default:
      return 'default';
  }
};

const getServiceIcon = (name: string) => {
  if (name.includes('API')) return <Cloud />;
  if (name.includes('Database')) return <Storage />;
  if (name.includes('Storage')) return <Storage />;
  if (name.includes('Cache')) return <Memory />;
  return <Cloud />;
};

const ServiceCard: React.FC<{ service: ServiceHealth }> = ({ service }) => {
  const isProcessingApi = service.name === 'Processing API';
  const details = service.details || {};

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor:
          service.status === 'healthy'
            ? '#10b981'
            : service.status === 'degraded'
            ? '#f59e0b'
            : service.status === 'unhealthy'
            ? '#ef4444'
            : '#6b7280',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getServiceIcon(service.name)}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {service.name}
            </Typography>
          </Box>
          <Chip
            icon={getStatusIcon(service.status)}
            label={service.status.charAt(0).toUpperCase() + service.status.slice(1)}
            color={getStatusColor(service.status)}
            size="small"
          />
        </Box>

        {service.latency_ms !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Speed fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Latency: {service.latency_ms}ms
            </Typography>
          </Box>
        )}

        {/* Processing API specific details */}
        {isProcessingApi && service.status !== 'unknown' && (
          <Box sx={{ mt: 2 }}>
            {details.version && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Version: {details.version}
              </Typography>
            )}
            {details.uptime_seconds !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Timer fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Uptime: {formatUptime(details.uptime_seconds)}
                </Typography>
              </Box>
            )}
            {details.ffmpeg_available !== undefined && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                FFmpeg: {details.ffmpeg_available ? 'Available' : 'Missing'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
              {details.jobs_processed !== undefined && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {details.jobs_processed.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Jobs Processed
                  </Typography>
                </Box>
              )}
              {details.jobs_in_queue !== undefined && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {details.jobs_in_queue}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    In Queue
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Error details */}
        {details.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {details.error}
          </Alert>
        )}

        {/* Unknown status message */}
        {service.status === 'unknown' && details.message && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {details.message}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Last checked: {new Date(service.checked_at).toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

const SystemStatus: React.FC = () => {
  const { data: health, isLoading, error, refetch, isFetching } = useSystemHealth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load system health: {(error as any)?.message || 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          System Status
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isFetching && <CircularProgress size={20} />}
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Status Banner */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          bgcolor:
            health?.overall_status === 'healthy'
              ? 'rgba(16, 185, 129, 0.1)'
              : health?.overall_status === 'degraded'
              ? 'rgba(245, 158, 11, 0.1)'
              : 'rgba(239, 68, 68, 0.1)',
          border: 1,
          borderColor:
            health?.overall_status === 'healthy'
              ? '#10b981'
              : health?.overall_status === 'degraded'
              ? '#f59e0b'
              : '#ef4444',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getStatusIcon(health?.overall_status || 'unknown')}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {health?.overall_status === 'healthy'
                ? 'All Systems Operational'
                : health?.overall_status === 'degraded'
                ? 'Some Systems Degraded'
                : 'System Issues Detected'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {health?.checked_at ? new Date(health.checked_at).toLocaleString() : 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Service Cards */}
      <Grid container spacing={3}>
        {health?.services.map((service) => (
          <Grid item xs={12} md={6} lg={4} key={service.name}>
            <ServiceCard service={service} />
          </Grid>
        ))}
      </Grid>

      {/* Auto-refresh indicator */}
      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Auto-refreshes every 30 seconds
        </Typography>
        {isFetching && <LinearProgress sx={{ width: 100 }} />}
      </Box>
    </Box>
  );
};

export default SystemStatus;
